/**
 * @file PayGroupDropdown.js
 * @description A dropdown component for selecting a pay group.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { fetchPayGroups } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const PayGroupDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedPayGroup,
    onPayGroupChange,
    isPublic = false,
    companyCode = null,
    required = false,
    errorMessage = null,
}) => {
    const [payGroups, setPayGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getPayGroups = async () => {
            setIsLoading(true);
            try {
                const data = await fetchPayGroups(tenantId, {
                    isPublic,
                    companyCode,
                });
                setPayGroups(data);
            } catch (error) {
                console.error('Could not fetch pay groups:', error);
                Toast.error(getText('msg_86a287e5a578'));
            } finally {
                setIsLoading(false);
            }
        };

        getPayGroups();
    }, [tenantId, isPublic, companyCode]);

    const getDefaultLabel = () => {
        const matchingPayGroup = payGroups.find(
            pg => pg.id === selectedPayGroup,
        );
        return matchingPayGroup?.name ?? 'Select Pay Group';
    };

    const payGroupOptions = useMemo(
        () => [
            { value: null, label: getText('msg_80b1758270d4') },
            ...payGroups.map(pg => ({ value: pg.id, label: pg.name })),
        ],
        [payGroups],
    );

    return (
        <fieldset
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
            style={{ position: 'relative' }}
        >
            <label htmlFor="pay-group-dropdown">
                {getText('msg_314ab9a7b294')}
                {required && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="pay-group-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={payGroupOptions}
                    value={selectedPayGroup}
                    onChange={onPayGroupChange}
                    disabled={payGroups.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </fieldset>
    );
};

export default PayGroupDropdown;
